// Tiny fetch client + token storage for the admin panel.
// Every call assumes the admin is signed in (via `setSession`). A failed call
// returns `{ ok: false, error, status }` and the page shows that error. There is
// no fallback data of any kind: an unreachable server is shown as unreachable.
//
// Endpoints marked "growth spec" below are the ones the October 2026 growth
// changes add (projects/GROWTH-IMPLEMENTATION.md). Their shapes follow the spec;
// where the spec does not fix a shape, the one assumed here is written next to
// the call so the server workstream can match it. Every new field is optional
// on read, so this console keeps working against a server that predates them
// and says "not reported" rather than inventing a value.

import { creditsToUsd, type TierId } from './tiers'

// API base. An explicit VITE_SIGNAL_BASE always wins. Otherwise a PRODUCTION
// build must NOT fall back to localhost — doing so silently broke the deployed
// admin (every call hit a dead http://localhost:3001 and CORS-failed), so it
// defaults to the hosted signal server. Only a dev build falls back to local.
const PROD_SIGNAL_BASE = 'https://api.plexiidesk.com'
// `||`, not `??`: Vercel's production env holds VITE_SIGNAL_BASE as an EMPTY
// string, which `??` kept, sending every call to the console's own origin.
const BASE =
  import.meta.env.VITE_SIGNAL_BASE ||
  (import.meta.env.PROD ? PROD_SIGNAL_BASE : 'http://localhost:3001')
const SESSION_KEY = 'plexii-admin.signal-session.v1'
// The pre-rename key. Read once, so renaming the key does not sign the operator
// out: getSession() falls back to it and setSession() then writes the new name
// and clears the old. Nothing but this line depends on the old key.
const LEGACY_SESSION_KEY = 'haptyx-admin.signal-session.v1'
// Set when an authenticated request comes back 401, i.e. the admin session has
// expired (the server's sessions live 12 hours). The login page reads and
// clears this to explain why the operator was bounced back to sign-in, instead
// of every page failing at once with no explanation.
export const SESSION_EXPIRED_KEY = 'plexii-admin.session-expired.v1'

/** The in-page event signalling a session change. Exported so the auth hook
 *  listens for the same string this file dispatches. */
export const SESSION_CHANGE_EVENT = 'plexii-admin-signal-session-change'

interface AdminSessionPayload {
  sessionToken: string
  admin: {
    id: string
    email: string
    name: string
    role: 'owner' | 'admin' | 'support'
    createdAt: number
    lastLoginAt: number | null
  }
}

export function getSession(): AdminSessionPayload | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY) ?? localStorage.getItem(LEGACY_SESSION_KEY)
    return raw ? (JSON.parse(raw) as AdminSessionPayload) : null
  } catch {
    return null
  }
}

export function setSession(s: AdminSessionPayload | null): void {
  if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s))
  else localStorage.removeItem(SESSION_KEY)
  // Always clear the old key, on sign-in and sign-out alike, so a stale session
  // under the old name cannot outlive a sign-out.
  localStorage.removeItem(LEGACY_SESSION_KEY)
  window.dispatchEvent(new CustomEvent(SESSION_CHANGE_EVENT))
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<{ ok: true; data: T } | { ok: false; error: string; status: number; body?: Record<string, unknown> }> {
  const session = getSession()
  // Say JSON only when there is a body. The server refuses a POST that claims
  // JSON and sends nothing (400 FST_ERR_CTP_EMPTY_JSON_BODY, before any route
  // runs), which silently broke every body-less action: End trial, Resend
  // verification, Mark verified, Close account, Restore, Remind, Refresh.
  const headers: Record<string, string> = init.body !== undefined && init.body !== null ? { 'content-type': 'application/json' } : {}
  if (session) headers.authorization = `Bearer ${session.sessionToken}`
  if (init.headers) Object.assign(headers, init.headers)
  try {
    const res = await fetch(`${BASE}${path}`, { ...init, headers })
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
    if (!res.ok) {
      // A 401 on a call we sent a token with means the admin session has
      // expired or been revoked. Clear it so the app's auth gate routes back
      // to the login screen, rather than every page showing a 401. We guard
      // on `session` so a wrong-password 401 at login (no token sent) does not
      // trip this path.
      // `session` was read when this request STARTED. Two requests can be in
      // flight with the same session — React StrictMode double-invokes mount
      // effects in development, so a page fires its loader twice — and both
      // then take this branch on a 401. The first clears the session and the
      // login page consumes the flag; the second would set it again afterwards,
      // leaving a stale "your session expired" notice for the next visit.
      // Re-reading here means only the request that still sees a session
      // raises it.
      if (res.status === 401 && session && getSession()) {
        try {
          localStorage.setItem(SESSION_EXPIRED_KEY, '1')
        } catch {
          // ignore storage failures
        }
        setSession(null)
      }
      return {
        ok: false,
        error: (body.error as string) || `HTTP ${res.status}`,
        status: res.status,
        // The rest of the answer, for the calls whose refusals carry data
        // (sendAdhocEmail reads the fresh numbers from a 409).
        body
      }
    }
    return { ok: true, data: body as unknown as T }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
      status: 0
    }
  }
}

// ── Auth ────────────────────────────────────────────────────────────────
export async function adminLogin(
  email: string,
  password: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await request<{ ok: true; sessionToken: string; admin: AdminSessionPayload['admin'] }>(
    '/admin/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) }
  )
  if (!res.ok) return { ok: false, error: res.error }
  setSession({ sessionToken: res.data.sessionToken, admin: res.data.admin })
  return { ok: true }
}

export async function adminLogout(): Promise<void> {
  const session = getSession()
  setSession(null)
  if (!session) return
  // Fire-and-forget — local clear is what matters.
  void fetch(`${BASE}/admin/auth/logout`, {
    method: 'POST',
    headers: { authorization: `Bearer ${session.sessionToken}` }
  })
}

// ── Users ───────────────────────────────────────────────────────────────
// What Stripe knows about an account's subscription.
export interface RemoteBilling {
  paying: boolean
  customerId: string | null
  subscriptionId: string | null
  subscriptionStatus: string | null
  intendedPlan: string | null
  billableSeats: number
  // Growth spec (assumed shape, optional): recorded at webhook time so the
  // console can show what each subscriber is actually charged. Absent on a
  // server that predates them; the console then says the price is unknown.
  /** Tier the live subscription is for (may differ from a hand-set tier). */
  plan?: string | null
  /** Billing interval of the live subscription. */
  interval?: 'month' | 'year' | null
  /** What Stripe charges per unit per interval, in cents. */
  unitAmountCents?: number | null
  /** Units Stripe charges for (seats on Team, 1 otherwise). */
  quantity?: number | null
  /** True when the subscriber kept an older (lower) price after the ladder
   *  changed. Section 1: existing subscribers keep their price until they
   *  change plan. */
  grandfathered?: boolean | null
}

export interface RemoteReferredBy {
  accountId: string
  email: string | null
  handle: string | null
  code: string | null
}

/** Referral figures for one account: the user-facing GET /referrals minus
 *  code and link (growth spec sections 4 and 4b). Every field is optional so a
 *  server on either revision of the spec is read without inventing values. */
export interface RemoteReferralStats {
  invited?: number
  verified?: number
  paid?: number
  /** AI credits earned from the section 4 welcome rewards. */
  creditsEarned?: number
  /** Section 4b: referred accounts that joined free and verified. */
  qualified?: number
  /** Section 4b: the US$ credit bank (subscriptions only, not AI credits). */
  creditBankUsd?: number
  creditEarnedUsd12m?: number
  creditCapUsd?: number
  /** Plan currently paid from the bank, and the date it is paid until. */
  planFundedByCredit?: string | null
  creditPaidUntil?: number | string | null
  /** Bank credit recorded for a paying user while Stripe is unset (dev/test). */
  pendingCreditUsd?: number | null
}

export interface RemoteAdminUser {
  id: string
  email: string
  handle: string | null
  tier: string
  status: 'active' | 'suspended' | 'pending'
  createdAt: string
  lastLoginAt: string | null
  createdAtMs?: number | null
  lastLoginAtMs?: number | null
  deviceCount: number
  shareCount: number
  inboxCount: number
  // What Stripe knows. `paying` is the distinction the console could not make
  // before: a tier on its own cannot tell a real subscriber from an account
  // someone set to team by hand, and in production every team-tier account was
  // the latter.
  billing: RemoteBilling
  // Growth spec section 3 (assumed field names, optional on read).
  emailVerified?: boolean
  emailVerifiedAt?: number | null
  // Growth spec section 4 (assumed field names, optional on read).
  referralCode?: string | null
  referredBy?: RemoteReferredBy | null
  referrals?: RemoteReferralStats | null
  // 14-day Team trial. Starts on email verification since the growth change.
  // Server-computed; admin sees days-left and can extend or force-end via the
  // trial endpoints below.
  trialActive: boolean
  trialDaysLeft: number
  trialStartedAt: number | null
  trialExpiresAt: number | null
  // Latest desktop telemetry check-in for this account. Null = the client has
  // never reported (pre-telemetry build, or not yet online on a capable one).
  telemetryReportedAt: number | null
  telemetryWidgets: number | null
  telemetryVersion: string | null
  telemetryPlatform: string | null
  onboardingCompleted: boolean | null
  onboardingModules: number | null
}

export async function listUsers() {
  return request<{ ok: true; users: RemoteAdminUser[] }>('/admin/users')
}

export async function patchUser(
  id: string,
  patch: { tier?: TierId; status?: RemoteAdminUser['status'] }
) {
  return request<{ ok: true; user: RemoteAdminUser }>(`/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  })
}

export interface RemoteConsoleAdmin {
  id: string
  email: string
  name: string
  role: 'owner' | 'admin' | 'support'
  createdAt: number
  lastLoginAt: number | null
}

// Console admins (the admin-panel team), distinct from app users above.
export async function listAdmins() {
  return request<{ ok: true; admins: RemoteConsoleAdmin[] }>('/admin/admins')
}

// Owner-only: add a console admin. The server returns a one-time temporary
// password to hand to the new admin, who signs in and should then change it.
export async function inviteAdmin(input: { email: string; name: string; role: 'admin' | 'support' }) {
  return request<{ ok: true; admin: RemoteConsoleAdmin; tempPassword: string }>('/admin/admins', {
    method: 'POST',
    body: JSON.stringify(input)
  })
}

// Extend the user's trial window by N days from NOW. Useful for both
// active and lapsed accounts — sets a fresh window irrespective of
// current expiry. Default 7d to match the suggested "give them another
// week" affordance in the UI.
export async function extendUserTrial(id: string, days: number = 7) {
  return request<{ ok: true; user: RemoteAdminUser }>(
    `/admin/users/${id}/trial/extend`,
    {
      method: 'POST',
      body: JSON.stringify({ days })
    }
  )
}

// Force-end an active trial. Sets expiry to just before now so the
// next desktop poll resolves to the stored tier (default 'free').
export async function endUserTrial(id: string) {
  return request<{ ok: true; user: RemoteAdminUser }>(
    `/admin/users/${id}/trial/end`,
    { method: 'POST' }
  )
}

// Growth spec section 3 (assumed endpoints). Re-send the verification email
// (code + link). The server applies its own 1/min, 10/day limits and answers
// 429 when hit; the page shows that error as-is.
export async function resendVerification(id: string) {
  return request<{ ok: true; sent?: boolean; alreadyVerified?: boolean }>(
    `/admin/users/${encodeURIComponent(id)}/verification/resend`,
    { method: 'POST' }
  )
}

// Mark an account's email verified by hand (support case: the user proved
// ownership another way). Starts the trial and the trial grant exactly as a
// real verification would. Returns the refreshed user row.
export async function markEmailVerified(id: string) {
  return request<{ ok: true; user: RemoteAdminUser }>(
    `/admin/users/${encodeURIComponent(id)}/verify`,
    { method: 'POST' }
  )
}

// ── Referral credit bank (growth spec section 4b; assumed endpoints) ─────
// US dollars, spendable only on subscriptions; separate from AI credits.
export interface RemoteCreditBankEntry {
  id: string
  at: number | string
  /** earned = a qualifying referral; redeemed = a month of plan paid from the
   *  bank; adjusted = an admin adjustment; pushed = moved to the Stripe
   *  customer balance (paying users). */
  kind: 'earned' | 'redeemed' | 'adjusted' | 'pushed' | (string & {})
  /** Signed: positive adds to the bank, negative takes from it. */
  usd: number
  reason?: string | null
  plan?: string | null
  referredAccountId?: string | null
  referredEmail?: string | null
  adminEmail?: string | null
  revokedAt?: number | string | null
  revokeReason?: string | null
}

export interface RemoteCreditBank {
  accountId: string
  balanceUsd: number
  earnedUsd12m: number
  capUsd: number
  qualified: number
  planFundedByCredit: string | null
  creditPaidUntil: number | string | null
  pendingCreditUsd?: number | null
  entries: RemoteCreditBankEntry[]
}

export async function getCreditBank(id: string) {
  return request<{ ok: true } & RemoteCreditBank>(`/admin/users/${encodeURIComponent(id)}/credit-bank`)
}

// Adjust the bank by a signed US$ amount. A reason is required; the server
// writes it to the audit log. Returns the refreshed bank.
export async function adjustCreditBank(id: string, usd: number, reason: string) {
  return request<{ ok: true } & RemoteCreditBank>(`/admin/users/${encodeURIComponent(id)}/credit-bank/adjust`, {
    method: 'POST',
    body: JSON.stringify({ usd, reason })
  })
}

// Revoke one earned entry (e.g. a referral found to be abuse). The server takes
// its amount back out of the bank and audit-logs the reason.
export async function revokeCreditBankEntry(id: string, entryId: string, reason: string) {
  return request<{ ok: true } & RemoteCreditBank>(
    `/admin/users/${encodeURIComponent(id)}/credit-bank/entries/${encodeURIComponent(entryId)}/revoke`,
    { method: 'POST', body: JSON.stringify({ reason }) }
  )
}

// ERASE an account and its data for good. Irreversible: only for a person who
// asked for erasure. Closing (archiveUser) is the default, as it is for people
// closing their own account.
export async function deleteUser(id: string) {
  return request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' })
}

// ── Closed accounts (archives) ──────────────────────────────────────────────
// Closing archives an account: unusable and unread, restorable until eraseAt,
// then erased by the server's retention sweep.
export interface RemoteArchive {
  id: string
  accountId: string
  email: string
  closedAt: number
  eraseAt: number
  archivedBy: 'self' | 'admin' | string
  rows: number
  files: number
}

export async function archiveUser(id: string) {
  return request<{ ok: true; notRestorable: string[] }>(`/admin/users/${encodeURIComponent(id)}/archive`, { method: 'POST' })
}

export async function listArchives() {
  return request<{ ok: true; retentionDays: number; archives: RemoteArchive[] }>('/admin/archives')
}

export async function restoreArchivedAccount(archiveId: string) {
  return request<{ ok: true; accountId: string; rows: number; notRestored: string[] }>(
    `/admin/archives/${encodeURIComponent(archiveId)}/restore`,
    { method: 'POST' }
  )
}

export async function eraseArchivedAccount(archiveId: string) {
  return request<{ ok: true }>(`/admin/archives/${encodeURIComponent(archiveId)}`, { method: 'DELETE' })
}

// ── Tickets ─────────────────────────────────────────────────────────────
export interface RemoteTicket {
  id: string
  accountId: string | null
  userEmail: string
  subject: string
  body: string
  status: 'open' | 'pending' | 'resolved' | 'closed'
  priority: 'low' | 'normal' | 'high' | 'urgent'
  category: string | null
  createdAt: string
  updatedAt: string
  replyCount: number
}

export interface RemoteTicketReply {
  id: string
  body: string
  authorEmail: string
  authorKind: 'admin' | 'user'
  createdAt: string
}

export async function listTickets() {
  return request<{ ok: true; tickets: RemoteTicket[] }>('/admin/tickets')
}

export async function getTicket(id: string) {
  return request<{ ok: true; ticket: RemoteTicket; replies: RemoteTicketReply[] }>(
    `/admin/tickets/${id}`
  )
}

export async function replyTicket(id: string, body: string) {
  return request<{ ok: true; reply: RemoteTicketReply }>(`/admin/tickets/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ body })
  })
}

export async function patchTicket(
  id: string,
  patch: { status?: RemoteTicket['status']; priority?: RemoteTicket['priority'] }
) {
  return request<{ ok: true; ticket: RemoteTicket }>(`/admin/tickets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  })
}

// ── Leads ───────────────────────────────────────────────────────────────
// Every form sent on the website: live session, demo, early access and the
// download-link form phones and tablets get. The site's /api/leads emails the
// team and posts the same lead to the server, so this is the same data the
// mailbox holds, in a form that can be filtered and worked through.
//
// Campaign fields are parsed server-side out of the page URL the form already
// sends. A lead that arrived without one reads as unknown — never guessed.
export type LeadKind = 'live' | 'demo' | 'early' | 'download'
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'won' | 'lost'

export const LEAD_KINDS: readonly LeadKind[] = ['live', 'demo', 'early', 'download']
export const LEAD_STATUSES: readonly LeadStatus[] = [
  'new',
  'contacted',
  'qualified',
  'won',
  'lost'
]

export const LEAD_KIND_LABEL: Record<LeadKind, string> = {
  live: 'Live session',
  demo: 'Demo request',
  early: 'Early access',
  download: 'Download link'
}

export interface RemoteLead {
  id: string
  kind: LeadKind
  name: string
  email: string
  emailDomain: string
  freeMail: boolean
  company: string | null
  role: string | null
  teamSize: string | null
  message: string | null
  page: string | null
  landingPath: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
  utmTerm: string | null
  clickId: string | null
  accountId: string | null
  status: LeadStatus
  ownerAdminId: string | null
  ownerAdminEmail: string | null
  notes: string | null
  receivedAt: string
  createdAt: string
  updatedAt: string
  submissionCount: number
}

export interface LeadCounts {
  total: number
  byStatus: Record<LeadStatus, number>
  byKind: Record<LeadKind, number>
}

export async function listLeads(
  params: { status?: LeadStatus | 'all'; kind?: LeadKind | 'all'; q?: string; limit?: number } = {}
) {
  const q = new URLSearchParams()
  if (params.status && params.status !== 'all') q.set('status', params.status)
  if (params.kind && params.kind !== 'all') q.set('kind', params.kind)
  if (params.q) q.set('q', params.q)
  if (params.limit) q.set('limit', String(params.limit))
  const qs = q.toString()
  return request<{ ok: true; leads: RemoteLead[]; total: number; counts: LeadCounts }>(
    `/admin/leads${qs ? `?${qs}` : ''}`
  )
}

export async function getLead(id: string) {
  return request<{ ok: true; lead: RemoteLead; history: RemoteLead[] }>(`/admin/leads/${id}`)
}

export async function patchLead(
  id: string,
  patch: { status?: LeadStatus; ownerAdminId?: string | null; notes?: string | null }
) {
  return request<{ ok: true; lead: RemoteLead }>(`/admin/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  })
}

// Owner role only on the server; a support session gets 403 and the page shows
// that error rather than hiding the button and pretending it worked.
export async function deleteLead(id: string) {
  return request<{ ok: true }>(`/admin/leads/${id}`, { method: 'DELETE' })
}

// ── Downloads ───────────────────────────────────────────────────────────
// Counted by Cloudflare on dl.plexiidesk.com, not by GA. GA's `download_click`
// is a click on a page we tag; it cannot see a consent-decliner, a click in
// the download-link email the mobile form sends, an old link, or a self-update.
/** A day's count per platform; null when Cloudflare was never asked about that day. */
export interface DownloadDay {
  date: string
  mac: number | null
  win: number | null
}

export type DownloadCoverage = 'full' | 'partial' | 'none'

/** One platform's stored figures for one day (server: downloadStats.ts). */
export interface DownloadPlatformDay {
  installs: number
  installsSelf: number
  updates: number
  updateChecks: number
  sampleInterval: number | null
  fetchedAt: string | null
}

export interface DailyDownloads {
  date: string
  coverage: DownloadCoverage
  coveredUntil: string | null
  mac: DownloadPlatformDay | null
  win: DownloadPlatformDay | null
}

export interface DownloadConfig {
  configured: boolean
  zoneId: string
  host: string
  selfIps: string[]
  reason: string | null
}

export interface DownloadSummary {
  config: DownloadConfig
  days: number
  installs: DownloadDay[]
  updateChecks: DownloadDay[]
  /** Absent from servers before 2026-10-30. */
  daily?: DailyDownloads[]
  coverage?: Record<DownloadCoverage, number>
  totals: {
    installs: { mac: number; win: number }
    /** What the self-IP filter removed. Shown, not hidden, so the filter is auditable. */
    installsSelf: { mac: number; win: number }
    updates: { mac: number; win: number }
    updateChecks: { mac: number; win: number }
  }
  /** Cloudflare's sampling rate. A count of 10 at interval 10 is ONE observed request. */
  sampleInterval: number
  lastFetchedAt: string | null
}

/** One line of the Conversions page (server: conversions.ts). */
export interface ConversionEvent {
  type: 'signup' | 'form' | 'checkout'
  at: string
  name: string | null
  email: string
  detail: string | null
  company: string | null
  source: string | null
  status: string
  accountId: string | null
  leadId: string | null
  recovered: boolean
}

export interface ConversionsReport {
  from: string
  to: string
  days: number
  totals: { signups: number; verified: number; forms: number; checkouts: number; paid: number; downloads: { mac: number; win: number } }
  events: ConversionEvent[]
  downloads: Array<{ date: string; mac: number | null; win: number | null }>
  downloadSampleInterval: number
  downloadsConfigured: boolean
}

export async function getConversions(days = 30) {
  return request<{ ok: true } & ConversionsReport>(`/admin/conversions?days=${days}`)
}

// ── Emails (server: emailSettings.ts) ──────────────────────────────────────

export type EmailFieldKind = 'line' | 'text' | 'paragraphs' | 'points'
export interface EmailField {
  key: string
  label: string
  kind: EmailFieldKind
  max: number
  maxItems?: number
  titleMax?: number
}
export interface EmailPoint {
  title: string
  body: string
}
export type EmailContent = Record<string, string | string[] | EmailPoint[]>
export type EmailGroup = 'trial' | 'signup' | 'checkout' | 'upgrade'

/** One automated email as the Emails page shows it. */
export interface ManagedEmail {
  key: string
  group: EmailGroup
  name: string
  who: string
  marketing: boolean
  fields: EmailField[]
  placeholders: Array<{ name: string; meaning: string }>
  always: string
  timing: { value: number; default: number; unit: 'hours' | 'days'; phrase: string; min: number; max: number }
  enabled: boolean
  enabledDefault: boolean
  content: EmailContent
  defaultContent: EmailContent
  changed: { content: boolean; timing: boolean; enabled: boolean }
  updatedAt: number | null
  updatedBy: string | null
  ignored: string | null
  stats: { total: number; last30: number; delivered: number; lastSentAt: number | null }
  /** Where the stats are counted from; absent from an older server. */
  records?: { src: 'lifecycle' | 'remarketing'; step: string }
}

export interface EmailsReport {
  emails: ManagedEmail[]
  unsubscribes: { total: number; last30: number }
  /** The moment "the last 30 days" starts from (ms); absent from an older server. */
  since?: number
  sender: { name: string; address: string }
  alwaysSent: Array<{ name: string; when: string }>
}

export async function getEmails() {
  return request<{ ok: true } & EmailsReport>('/admin/emails')
}

export async function saveEmail(key: string, change: { content?: EmailContent; timing?: number; enabled?: boolean }) {
  return request<{ ok: true; email: ManagedEmail }>(`/admin/emails/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(change) })
}

export async function resetEmail(key: string) {
  return request<{ ok: true; email: ManagedEmail }>(`/admin/emails/${encodeURIComponent(key)}`, { method: 'DELETE' })
}

export async function previewEmail(key: string, draft: { content: EmailContent; timing: number }) {
  return request<{ ok: true; subject: string; html: string; text: string }>(`/admin/emails/${encodeURIComponent(key)}/preview`, {
    method: 'POST',
    body: JSON.stringify(draft)
  })
}

export async function sendTestEmail(key: string, draft: { content: EmailContent; timing: number }) {
  return request<{ ok: true; to: string; delivered: boolean; provider: string }>(`/admin/emails/${encodeURIComponent(key)}/test`, {
    method: 'POST',
    body: JSON.stringify(draft)
  })
}

// ── Sent log and sending from the console (server: emailSendLog.ts, emailCampaigns.ts)

export type EmailLogStatus = 'Sent' | 'Failed' | 'Not sent' | 'Not confirmed'

export interface EmailLogRow {
  id: string
  at: number
  to: string
  toAccountId: string | null
  toName: string | null
  kind: string
  label: string
  category: string
  subject: string
  status: EmailLogStatus
  reason: string | null
  provider: string
  providerMessageId: string | null
  campaignId: string | null
  campaignSubject: string | null
  sentBy: string
}

export interface EmailLog {
  rows: EmailLogRow[]
  total: number
  truncated: boolean
  from: number | null
  to: number
  coverage: { completeSince: number | null; note: string }
}

export async function getEmailLog(days: number | 'all') {
  return request<{ ok: true } & EmailLog>(`/admin/emails/log?days=${days}`)
}

export async function checkEmailDelivery(id: string) {
  return request<{ ok: true; known: boolean; lastEvent?: string | null; message?: string; checkedAt?: number }>(
    `/admin/emails/log/${encodeURIComponent(id)}/delivery`
  )
}

export type CampaignPurpose = 'announcement' | 'notice'
export interface CampaignContent {
  subject: string
  paragraphs: string[]
  button: { label: string; url: string } | null
}
export interface AudienceFacts {
  facts: Record<string, { unsubscribed: boolean; lastEmailedAt: number | null; sent30: number }>
  canSendAnnouncements: boolean
  maxRecipients: number
  placeholders: Record<string, string>
}

export async function getEmailAudience() {
  return request<{ ok: true } & AudienceFacts>('/admin/emails/audience')
}

export interface LeftOut {
  accountId: string
  email: string | null
  name: string | null
  reason: string
}

export async function previewAdhocEmail(draft: { purpose: CampaignPurpose; content: CampaignContent; accountId?: string }) {
  return request<{ ok: true; subject: string; html: string; text: string }>('/admin/emails/send/preview', { method: 'POST', body: JSON.stringify(draft) })
}

export async function checkAdhocAudience(purpose: CampaignPurpose, accountIds: string[]) {
  return request<{ ok: true; willSend: number; skipped: LeftOut[] }>('/admin/emails/send/check', {
    method: 'POST',
    body: JSON.stringify({ purpose, accountIds })
  })
}

export async function testAdhocEmail(draft: { purpose: CampaignPurpose; content: CampaignContent }) {
  return request<{ ok: true; to: string; delivered: boolean; provider: string }>('/admin/emails/send/test', { method: 'POST', body: JSON.stringify(draft) })
}

export type RecipientStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'not_sent' | 'skipped' | 'cancelled' | 'unknown'
export interface CampaignSummary {
  id: string
  purpose: CampaignPurpose
  subject: string
  status: 'sending' | 'paused' | 'cancelled' | 'done'
  pausedReason: string | null
  createdBy: string
  createdAt: number
  finishedAt: number | null
  counts: Record<RecipientStatus, number> & { total: number }
}
export interface CampaignDetail extends CampaignSummary {
  content: CampaignContent
  recipients: Array<{ accountId: string; email: string; name: string | null; status: RecipientStatus; reason: string | null; sendId: string | null; updatedAt: number }>
}

/** The send itself. A 409 carries `check` with the numbers as they are now. */
export async function sendAdhocEmail(input: { purpose: CampaignPurpose; content: CampaignContent; accountIds: string[]; confirmCount: number }) {
  const res = await request<{ ok: true; campaign: CampaignDetail }>('/admin/emails/send', { method: 'POST', body: JSON.stringify(input) })
  if (res.ok) return res
  const check = (res.body?.check as { willSend: number; skipped: LeftOut[] } | undefined) ?? null
  return { ...res, check }
}

export async function listEmailCampaigns() {
  return request<{ ok: true; campaigns: CampaignSummary[] }>('/admin/emails/campaigns')
}

export async function getEmailCampaign(id: string) {
  return request<{ ok: true; campaign: CampaignDetail }>(`/admin/emails/campaigns/${encodeURIComponent(id)}`)
}

export async function stopEmailCampaign(id: string) {
  return request<{ ok: true; campaign: CampaignDetail }>(`/admin/emails/campaigns/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: '{}' })
}

export async function resumeEmailCampaign(id: string) {
  return request<{ ok: true; campaign: CampaignDetail }>(`/admin/emails/campaigns/${encodeURIComponent(id)}/resume`, { method: 'POST', body: '{}' })
}

/** One past lead, read back from its notification email. `at` is required. */
export interface LeadImportItem {
  kind: 'live' | 'demo' | 'early' | 'download'
  name?: string
  email: string
  company?: string
  role?: string
  teamSize?: string
  message?: string
  page?: string
  at: string
}

export async function importLeads(leads: LeadImportItem[], source: string) {
  return request<{ ok: true; imported: number; duplicates: number; errors: Array<{ index: number; error: string }> }>(
    '/admin/leads/import',
    { method: 'POST', body: JSON.stringify({ leads, source }) }
  )
}

export async function getDownloads(days = 30) {
  return request<{ ok: true; downloads: DownloadSummary; metrics: RemoteDailyMetric[] }>(
    `/admin/downloads?days=${days}`
  )
}

export async function refreshDownloads(days = 30) {
  return request<{ ok: true; rowsWritten: number; sampleInterval: number; downloads: DownloadSummary }>(
    `/admin/downloads/refresh?days=${days}`,
    { method: 'POST' }
  )
}

// ── Metrics ─────────────────────────────────────────────────────────────
export interface RemoteDailyMetric {
  date: string
  signups: number
  dau: number
  shareLinksCreated: number
  speeddeckPresses: number
  aiCalls: number
}

/**
 * What the active-accounts figure can see. Accounts that signed in on a day
 * always count for it; on days they only used the app, an account counts only
 * if it has usage reporting turned on. So dau is a floor, and the console must
 * label it with these numbers rather than present it as everyone.
 */
export interface ActivityBasis {
  usageReportingAccounts: number
  totalAccounts: number
}

export async function listMetrics() {
  return request<{ ok: true; days: RemoteDailyMetric[]; activityBasis?: ActivityBasis }>('/admin/metrics')
}

// ── Usage (real telemetry aggregate) ────────────────────────────────────
export interface UsageSummary {
  reportingUsers: number
  lastReportedAt: number | null
  totals: {
    widgets: number
    tasks: number
    folders: number
    focusSessions: number
    focusMinutes: number
    aiCalls: number
  }
  averages: {
    widgetsPerUser: number
    tasksPerUser: number
    foldersPerUser: number
    focusMinutesPerUser: number
    aiCallsPerUser: number
  }
  widgetsByKind: Array<{ kind: string; count: number }>
  platforms: Array<{ platform: string; users: number }>
  versions: Array<{ version: string; users: number }>
}

export async function listUsage() {
  return request<{ ok: true; usage: UsageSummary; usageReportingAccounts?: number }>('/admin/usage')
}

// ── Crash reports (opt-in, kept 30 days) ────────────────────────────────
export interface RemoteCrash {
  id: string
  accountId: string
  ts: number
  receivedAt: number
  source: string
  kind: string
  message: string
  stack: string | null
  componentStack: string | null
  appVersion: string | null
  platform: string | null
  context: string | null
}

export async function listCrashes(limit = 200) {
  return request<{ ok: true; crashes: RemoteCrash[]; retentionDays: number }>(`/admin/crashes?limit=${limit}`)
}

// ── Invites (share recipients + the invite send log; no engagement tracking) ──
export interface RemoteInvite {
  recipientId: string
  email: string
  handle: string | null
  status: 'invited' | 'accepted'
  invitedAt: number
  acceptedAt: number | null
  shareToken: string
  shareKind: string | null
  shareRevoked: boolean
  ownerEmail: string | null
  ownerHandle: string | null
  sendCount: number
  delivered: boolean
  lastSentAt: number | null
}

export interface RemoteEmailSend {
  id: string
  recipientId: string | null
  shareToken: string | null
  toEmail: string
  kind: 'invite' | 'reminder'
  subject: string
  provider: string
  providerMessageId: string | null
  delivered: boolean
  error: string | null
  createdAt: number
}

export interface RemoteInviteDetail extends RemoteInvite {
  sends: RemoteEmailSend[]
}

export async function listInvites(pendingOnly = false) {
  const q = pendingOnly ? '?pending=1' : ''
  return request<{ ok: true; invites: RemoteInvite[] }>(`/admin/invites${q}`)
}

export async function getInvite(recipientId: string) {
  return request<{ ok: true; invite: RemoteInviteDetail }>(`/admin/invites/${recipientId}`)
}

// Re-send the invite email to a pending recipient (tracked as a reminder).
export async function remindInvite(recipientId: string) {
  return request<{ ok: true; delivered: boolean; invite: RemoteInviteDetail }>(
    `/admin/invites/${recipientId}/remind`,
    { method: 'POST' }
  )
}

// ── Public plans (what the server actually serves the brochure) ──────────
// GET /public/plans: the server's plan defaults merged with admin overrides.
// The Subscriptions page shows it beside the contract ladder so drift between
// the spec and production is visible.
export interface RemotePublicPlan {
  id: string
  name: string
  priceMonthly: number | null
  priceYearly: number | null
  tagline?: string
  active?: boolean
  unit?: string
}
export async function listPublicPlans() {
  return request<{ ok: true; plans: RemotePublicPlan[] }>('/public/plans')
}

// ── Plans + capability overrides ────────────────────────────────────────
export interface RemotePlanOverride {
  tier: string
  priceMonthly: number | null
  priceYearly: number | null
  tagline: string | null
  active: boolean | null
  updatedAt: number
}
export async function listPlanOverrides() {
  return request<{ ok: true; overrides: RemotePlanOverride[] }>('/admin/plans')
}
export async function patchPlan(tier: string, body: Partial<RemotePlanOverride>) {
  return request<{ ok: true }>(`/admin/plans/${tier}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  })
}
export async function clearPlanOverrides() {
  return request<{ ok: true }>('/admin/plans', { method: 'DELETE' })
}

export interface RemoteCapabilityOverride {
  capabilityKey: string
  tier: string
  value: boolean | number | string
  updatedAt: number
}
export async function listCapabilityOverrides() {
  return request<{ ok: true; overrides: RemoteCapabilityOverride[] }>(
    '/admin/capabilities'
  )
}
export async function upsertCapability(input: {
  capabilityKey: string
  tier: string
  value: boolean | number | string
}) {
  return request<{ ok: true }>('/admin/capabilities', {
    method: 'PATCH',
    body: JSON.stringify(input)
  })
}
export async function clearCapabilityOverrides() {
  return request<{ ok: true }>('/admin/capabilities', { method: 'DELETE' })
}

// ── Per-user access (capability resolution + overrides) ──────────────────
// A single capability value: an on/off flag, a numeric limit, or a
// qualitative label like "Unlimited".
export type CapabilityValue = boolean | number | string

export interface AccountCapabilities {
  // The tier stored on the account.
  storedTier: string
  // The tier actually in force right now (e.g. an active trial can lift it).
  effectiveTier: string
  // The effective value this user gets for every capability right now:
  // tier defaults + tier overrides + this user's own overrides.
  resolved: Record<string, CapabilityValue>
  // Only the keys explicitly set for THIS user. Everything else is inherited.
  overrides: Record<string, CapabilityValue>
}

// Read a single account's resolved capabilities and its per-user overrides.
// Requires the support role server-side.
export async function getAccountCapabilities(id: string) {
  return request<{ ok: true } & AccountCapabilities>(
    `/admin/accounts/${encodeURIComponent(id)}/capabilities`
  )
}

// Set or clear one per-user capability override. Pass a concrete value to
// override, or null to CLEAR the override and revert this user to the tier
// default. Returns the refreshed resolution (same shape as the GET). Requires
// the admin role server-side.
export async function setAccountCapability(
  id: string,
  capabilityKey: string,
  value: CapabilityValue | null
) {
  return request<{ ok: true } & AccountCapabilities>(
    `/admin/accounts/${encodeURIComponent(id)}/capabilities`,
    { method: 'PUT', body: JSON.stringify({ capabilityKey, value }) }
  )
}

// ── Marketplace ─────────────────────────────────────────────────────────
export interface RemoteMarketplaceItem {
  id: string
  kind: 'widget' | 'template'
  title: string
  blurb: string
  authorHandle: string | null
  priceCents: number
  status: 'draft' | 'review' | 'published' | 'unlisted'
  installCount: number
  rating: number
  tags: string[]
  createdAt: string
  updatedAt: string
}
export async function listMarketplace() {
  return request<{ ok: true; items: RemoteMarketplaceItem[] }>('/admin/marketplace')
}
export async function patchMarketplace(
  id: string,
  patch: {
    title: string
    blurb: string
    priceCents: number
    status: RemoteMarketplaceItem['status']
    tags: string[]
  }
) {
  return request<{ ok: true; item: RemoteMarketplaceItem }>(`/admin/marketplace/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  })
}

// ── Organizations / RBAC ─────────────────────────────────────────────────
export type OrgRole = 'owner' | 'admin' | 'member' | 'guest'

export interface RemoteOrg {
  id: string
  name: string
  slug: string
  personal: boolean
  memberCount: number
  createdAt: string | number
}

export interface RemoteOrgMember {
  accountId: string
  displayName: string | null
  handle: string | null
  email: string | null
  role: OrgRole
  addedAt: string | number | null
}

// Office and ACL-grant shapes are defined by the org directory and are rendered
// defensively (any string/number field is surfaced) rather than assumed, so the
// page keeps working if the server adds fields.
export type RemoteOrgOffice = Record<string, unknown>
export type RemoteAclGrant = Record<string, unknown>

export interface RemoteOrgDetail {
  org: RemoteOrg
  members: RemoteOrgMember[]
  offices: RemoteOrgOffice[]
  acl: { onOrg: RemoteAclGrant[]; toOrg: RemoteAclGrant[] }
}

export async function listOrgs() {
  return request<{ ok: true; orgs: RemoteOrg[] }>('/admin/orgs')
}

export async function getOrg(id: string) {
  return request<{ ok: true } & RemoteOrgDetail>(`/admin/orgs/${id}`)
}

// PATCH a member's role. A 409 means the change would leave the org with no
// owner (last-owner demote); the page surfaces that inline.
export async function patchOrgMemberRole(orgId: string, accountId: string, role: OrgRole) {
  return request<{ ok: true } & RemoteOrgDetail>(
    `/admin/orgs/${orgId}/members/${encodeURIComponent(accountId)}/role`,
    { method: 'PATCH', body: JSON.stringify({ role }) }
  )
}

// Remove a member. A 409 means it would remove the last owner.
export async function removeOrgMember(orgId: string, accountId: string) {
  return request<{ ok: true } & RemoteOrgDetail>(
    `/admin/orgs/${orgId}/members/${encodeURIComponent(accountId)}`,
    { method: 'DELETE' }
  )
}

// ── Per-org access (capability overrides) ─────────────────────────────────
// An org has no tier of its own, so there is no single resolved value. The
// server returns only the keys the org has explicitly set; everything else
// inherits the plan and per-user resolution at the point the entitlement is
// actually checked. Entitlements resolve tier < org < user.
export interface OrgCapabilities {
  orgId: string
  // Only the keys explicitly set for THIS org. Everything else inherits.
  overrides: Record<string, CapabilityValue>
}

// Read one org's explicit capability overrides. Requires the support role
// server-side.
export async function getOrgCapabilities(id: string) {
  return request<{ ok: true } & OrgCapabilities>(
    `/admin/orgs/${encodeURIComponent(id)}/capabilities`
  )
}

// Set or clear one org-level capability override. Pass a concrete value to
// set the org override, or null to CLEAR it so the capability falls back to
// the plan and per-user resolution. Returns the refreshed override set (same
// shape as the GET). Requires the admin role server-side.
export async function setOrgCapability(
  id: string,
  capabilityKey: string,
  value: CapabilityValue | null
) {
  return request<{ ok: true } & OrgCapabilities>(
    `/admin/orgs/${encodeURIComponent(id)}/capabilities`,
    { method: 'PUT', body: JSON.stringify({ capabilityKey, value }) }
  )
}

// ── AI spend / credits + brain overview ──────────────────────────────────
// Denomination: since the growth change balances are in CREDITS (1 credit =
// US$0.01 of billed usage, billed = Anthropic list cost x AI_CREDIT_MARKUP).
// Older servers report the same ledger in billed US dollars (`balanceUsd` etc).
// The console converts those at exactly 100 credits per billed dollar, which
// is the definition of a credit, not an estimate.
export interface RemoteAiAccount {
  accountId: string
  displayName: string | null
  handle: string | null
  email: string | null
  calls: number
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  rawCostUsd: number
  billedUsd: number
  // Legacy USD ledger (null when the account has no credit row yet).
  balanceUsd?: number | null
  grantedUsd?: number | null
  spentUsd?: number | null
  // Growth spec (assumed, optional): the credit ledger in credits.
  tier?: string | null
  balance?: number | null
  buckets?: RemoteCreditBucket[] | null
}

export async function listAiUsage() {
  return request<{ ok: true; accounts: RemoteAiAccount[] }>('/admin/ai-usage')
}

export interface RemoteAiModelRow {
  model: string
  calls: number
  inputTokens: number
  outputTokens: number
  rawCostUsd: number
  billedUsd: number
}

export interface RemoteAiDailyRow {
  date: string
  calls: number
  inputTokens: number
  outputTokens: number
  rawCostUsd: number
  billedUsd: number
}

// Growth spec (assumed, optional): per-tier allowance usage for the current
// period. `allowanceCredits` is the total allowance issued to accounts on the
// tier this period; `allowanceUsedCredits` how much of it was spent.
export interface RemoteAiTierUsage {
  tier: string
  accounts: number
  /** The accounts behind the row, so it can open them and their grants. */
  accountIds?: string[]
  calls?: number
  allowanceCredits: number
  allowanceUsedCredits: number
  bonusUsedCredits?: number
  purchasedUsedCredits?: number
  rawCostUsd?: number
  billedUsd?: number
}

// Growth spec (assumed, optional): one-off top-up purchases in the window.
export interface RemoteTopupSummary {
  count: number
  revenueUsd: number
  credits: number
  byPack?: Array<{ usd: number; count: number }>
}

export interface RemoteAiOverview {
  totals: {
    calls: number
    inputTokens: number
    outputTokens: number
    cacheReadTokens: number
    cacheWriteTokens: number
    rawCostUsd: number
    billedUsd: number
  }
  credits: {
    accountsWithCredits: number
    // Legacy USD ledger.
    grantedUsd?: number
    balanceUsd?: number
    spentUsd?: number
    // Growth spec (assumed, optional): the same totals in credits.
    granted?: number
    balance?: number
    spent?: number
  }
  byModel: RemoteAiModelRow[]
  daily: RemoteAiDailyRow[]
  // Growth spec (assumed, optional).
  markup?: number
  byTier?: RemoteAiTierUsage[]
  topups?: RemoteTopupSummary
}

export async function getAiOverview(days = 30) {
  return request<{ ok: true; overview: RemoteAiOverview }>(`/admin/ai-overview?days=${days}`)
}

// One credit bucket, as in GET /ai/credits (growth spec section 2).
export interface RemoteCreditBucket {
  kind: 'allowance' | 'bonus' | 'purchased' | (string & {})
  credits: number
  expiresAt: number | string | null
  // Assumed, optional: where the bucket came from (trial, referral, topup,
  // admin) and when it was granted.
  source?: string | null
  grantedAt?: number | string | null
}

// GET /admin/accounts/:id/credits (growth spec, assumed): the admin view of
// GET /ai/credits for one account. `allowance` is the monthly allowance in
// credits; the spec leaves its shape open, so an object with used/remaining is
// accepted too.
export interface RemoteAccountCredits {
  accountId: string
  tier: string
  balance: number
  buckets: RemoteCreditBucket[]
  allowance: number | { monthly: number; used?: number; remaining?: number } | null
  resetsAt: number | string | null
}

export async function getAccountCredits(accountId: string) {
  return request<{ ok: true } & RemoteAccountCredits>(
    `/admin/accounts/${encodeURIComponent(accountId)}/credits`
  )
}

// Grant credits to an account. Growth spec: amounts are in CREDITS. The body
// also carries `usd` (= credits / 100, the same value by definition) so the
// grant works against a server that still takes the legacy USD body; a server
// on the new shape reads `credits` and ignores `usd`. `reason` goes to the
// audit log. Grants only: the server refuses non-positive amounts.
export async function grantCredits(accountId: string, credits: number, reason: string) {
  return request<{
    ok: true
    accountId: string
    grantedCredits?: number
    balance?: number
    // Legacy response fields.
    grantedUsd?: number
    balanceUsd?: number
  }>(`/admin/accounts/${encodeURIComponent(accountId)}/credits`, {
    method: 'POST',
    body: JSON.stringify({ credits, usd: creditsToUsd(credits), reason: reason || undefined })
  })
}

// GET /admin/ai-usage/log (growth spec, assumed): the metered-call ledger
// (ai_usage_log), newest first. Optional accountId filter.
export interface RemoteAiUsageEntry {
  id: string
  at: number | string
  accountId: string
  email?: string | null
  tier?: string | null
  model: string
  inputTokens: number
  outputTokens: number
  cacheReadTokens?: number
  cacheWriteTokens?: number
  rawCostUsd: number
  billedUsd: number
  credits?: number
  bucket?: string | null
}

export async function listAiUsageLog(params: { accountId?: string; limit?: number } = {}) {
  const q = new URLSearchParams()
  if (params.accountId) q.set('accountId', params.accountId)
  q.set('limit', String(params.limit ?? 100))
  return request<{ ok: true; entries: RemoteAiUsageEntry[] }>(`/admin/ai-usage/log?${q.toString()}`)
}

// ── Funnel (growth spec section 8) ───────────────────────────────────────
// GET /admin/funnel?from&to returns counts per event. The spec fixes the
// events, not the body, so both forms are accepted: `steps: [{event, count}]`
// or `counts: {event: count}`. from/to are sent as UTC calendar days
// (YYYY-MM-DD), both inclusive.
export const FUNNEL_STEPS = [
  'signup',
  'email_verified',
  'trial_started',
  'first_launch',
  'trial_credits_exhausted',
  'checkout_started',
  'subscription_started'
] as const

export const FUNNEL_SIDE_EVENTS = [
  'topup_purchased',
  'referral_signup',
  'referral_rewarded',
  'referral_qualified',
  'credit_bank_earned',
  'credit_bank_redeemed',
  'pending_build_used'
] as const

export type FunnelEvent = (typeof FUNNEL_STEPS)[number] | (typeof FUNNEL_SIDE_EVENTS)[number]

export interface RemoteFunnel {
  from?: string | number
  to?: string | number
  // `usd` is the summed amount where the event carries one (credit_bank_earned
  // {usd}, topup_purchased {amount}); assumed, optional.
  steps?: Array<{ event: string; count: number; accounts?: number; usd?: number; recovered?: number }>
  counts?: Record<string, number>
}

export async function getFunnel(from: string, to: string) {
  const q = new URLSearchParams({ from, to })
  return request<{ ok: true } & RemoteFunnel>(`/admin/funnel?${q.toString()}`)
}

/** What a funnel history recovery added (POST /admin/funnel/backfill). */
export interface FunnelBackfillResult {
  signup: number
  email_verified: number
  trial_started: number
  checkout_started: number
  subscription_started: number
  stripeSessionsSeen: number
  stripeUnmatched: number
  stripeError?: string
}

export async function backfillFunnel() {
  return request<{ ok: true } & FunnelBackfillResult>('/admin/funnel/backfill', { method: 'POST' })
}

// ── Meetings (live / ephemeral) ──────────────────────────────────────────
export interface RemoteMeetingParticipant {
  accountId: string
  displayName: string | null
  handle: string | null
}

export interface RemoteMeeting {
  roomId: string
  participantCount: number
  participants: RemoteMeetingParticipant[]
}

export async function listMeetings() {
  return request<{ ok: true; meetings: RemoteMeeting[]; note: string }>('/admin/meetings')
}

// ── Conversations (metadata only, no message bodies) ─────────────────────
export type ConversationKind = 'dm' | 'space' | 'channel'

export interface RemoteConversation {
  id: string
  kind: ConversationKind
  title: string | null
  orgId: string | null
  memberCount: number
  messageCount: number
  createdAt: string | number | null
  lastMessageAt: string | number | null
}

export interface RemoteConversationMember {
  accountId: string
  displayName: string | null
  handle: string | null
  email: string | null
}

export interface RemoteConversationDetail {
  conversation: RemoteConversation
  members: RemoteConversationMember[]
  messageStats: {
    total: number
    deleted: number
    senderCount: number
    firstMessageAt: string | number | null
    lastMessageAt: string | number | null
  }
}

export async function listConversations() {
  return request<{ ok: true; conversations: RemoteConversation[] }>('/admin/conversations')
}

export async function getConversation(id: string) {
  return request<{ ok: true } & RemoteConversationDetail>(
    `/admin/conversations/${encodeURIComponent(id)}`
  )
}

// ── Audit log (tamper-evident admin action trail) ─────────────────────────
// One row of the hash-chained audit log. `prevHash`/`hash` chain each event to
// the one before it, so the server can prove the log was not edited after the
// fact. `detail` is a JSON string or plain text describing the action.
export interface AuditEvent {
  id: string
  orgId: string | null
  actorAccountId: string | null
  action: string
  targetType: string | null
  targetId: string | null
  detail: string | null
  ip: string | null
  userAgent: string | null
  prevHash: string | null
  hash: string
  createdAt: number
}

// The result of verifying the hash chain. `ok` true means every event links
// cleanly to its predecessor. When it is false the server names the first
// event where the chain broke and why.
export interface AuditIntegrity {
  ok: boolean
  checked: number
  brokenAt?: string
  reason?: string
}

// Filters shared by the list and export endpoints. `action` is a PREFIX match
// (e.g. "admin." matches every admin action). `since`/`until` are unix ms.
export interface AuditQuery {
  orgId?: string
  actor?: string
  action?: string
  since?: number
  until?: number
  limit?: number
}

function auditQueryString(params: AuditQuery): string {
  const q = new URLSearchParams()
  if (params.orgId) q.set('orgId', params.orgId)
  if (params.actor) q.set('actor', params.actor)
  if (params.action) q.set('action', params.action)
  if (params.since != null) q.set('since', String(params.since))
  if (params.until != null) q.set('until', String(params.until))
  if (params.limit != null) q.set('limit', String(params.limit))
  const s = q.toString()
  return s ? `?${s}` : ''
}

export async function getAudit(params: AuditQuery = {}) {
  return request<{ ok: true; events: AuditEvent[]; integrity: AuditIntegrity }>(
    `/admin/audit${auditQueryString(params)}`
  )
}

export async function verifyAudit() {
  return request<{ ok: true; integrity: AuditIntegrity }>('/admin/audit/verify')
}

// The URL of the export endpoint for a given filter + format. The endpoint is
// auth-gated, so this URL cannot be used in a plain `<a href>` (no token would
// be sent). Use `downloadAuditExport` instead, which attaches the Bearer token.
export function auditExportUrl(params: AuditQuery, format: 'csv' | 'json'): string {
  const q = auditQueryString(params)
  const sep = q ? '&' : '?'
  return `${BASE}/admin/audit/export${q}${sep}format=${format}`
}

// Download the filtered export as a file. The export endpoint returns a raw
// file body (text/csv or application/json) rather than the usual {ok,...}
// envelope, and it requires the admin Bearer token, so we fetch it with the
// Authorization header, turn the body into a blob, and trigger a browser
// download via a temporary object URL. Returns an honest error string on
// failure rather than a silently empty file.
export async function downloadAuditExport(
  params: AuditQuery,
  format: 'csv' | 'json'
): Promise<{ ok: true } | { ok: false; error: string }> {
  const session = getSession()
  if (!session) return { ok: false, error: 'Not signed in' }
  try {
    const res = await fetch(auditExportUrl(params, format), {
      headers: { authorization: `Bearer ${session.sessionToken}` }
    })
    if (!res.ok) {
      let msg = `HTTP ${res.status}`
      try {
        const body = (await res.json()) as { error?: string }
        if (body.error) msg = body.error
      } catch {
        // Non-JSON error body; keep the status message.
      }
      return { ok: false, error: msg }
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    a.download = `plexii-audit-${stamp}.${format}`
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Revoke on the next tick so the click has resolved.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    return { ok: true }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) }
  }
}

// ── Records behind a figure (GET /admin/explore) ───────────────────────
// Every figure in the console opens the rows it was counted from: a record
// set narrowed by filters. Each record names the records it points at and the
// sets related to it, so the console can keep opening deeper (Drill.tsx).
export type ExploreCell = string | number | boolean | null
export type ExploreFormat = 'text' | 'int' | 'usd' | 'credits' | 'datetime' | 'date' | 'bool' | 'mono'

export interface ExploreRef {
  set: string
  filters: Record<string, string>
}

export interface ExploreFact {
  label: string
  v: ExploreCell
  format?: ExploreFormat
  ref?: ExploreRef
  wide?: boolean
}

export interface ExploreRow {
  id: string
  cells: Record<string, ExploreCell>
  refs?: Record<string, ExploreRef>
  facts: ExploreFact[]
  related: Array<{ label: string; ref: ExploreRef }>
}

export interface ExploreMeasure {
  key: string
  label: string
  value: number
  format: ExploreFormat
}

export interface ExploreResult {
  set: string
  title: string
  basis: string
  filters: string[]
  total: number
  measures: ExploreMeasure[]
  columns: Array<{ key: string; label: string; format?: ExploreFormat }>
  rows: ExploreRow[]
  offset: number
  limit: number
  note: string | null
}

export async function exploreRecords(ref: ExploreRef, opts: { limit?: number; offset?: number } = {}) {
  const q = new URLSearchParams({ set: ref.set, ...ref.filters })
  if (opts.limit !== undefined) q.set('limit', String(opts.limit))
  if (opts.offset) q.set('offset', String(opts.offset))
  return request<{ ok: true } & ExploreResult>(`/admin/explore?${q.toString()}`)
}

export async function exploreCounts(refs: ExploreRef[]) {
  return request<{ ok: true; counts: Array<number | null> }>(
    `/admin/explore/counts?refs=${encodeURIComponent(JSON.stringify(refs))}`
  )
}

// ── Growth: the goal, the funnel trace, the Today list (server: adminGrowth.ts) ──

/** A figure on a growth view: how many, and the accounts it was counted from. */
export interface GrowthCounted {
  count: number
  ref: ExploreRef
}

export interface GrowthGoal {
  startedAt: number
  baseline: number
  target: number
  payingNow: GrowthCounted
  /** payingNow − baseline: gained, net of anyone who stopped paying. */
  net: number
  newSince: GrowthCounted
  bySource: Array<{ key: string; label: string } & GrowthCounted>
}

export async function getGrowthGoal() {
  return request<{ ok: true; goal: GrowthGoal }>('/admin/growth/goal')
}

export async function setGrowthGoalTarget(target: number) {
  return request<{ ok: true; goal: GrowthGoal }>('/admin/growth/goal', { method: 'PUT', body: JSON.stringify({ target }) })
}

export interface GrowthTraceStep {
  key: 'joined' | 'confirmed' | 'returned' | 'checkout' | 'paying'
  label: string
  means: string
  from: number
  reached: GrowthCounted
  dropped: GrowthCounted
  unknown: GrowthCounted
  waiting: GrowthCounted
  why: Array<{ key: string; label: string } & GrowthCounted>
}

export async function getGrowthTrace(from: string, to: string) {
  const q = new URLSearchParams({ from, to })
  return request<{ ok: true; from: number; to: number; steps: GrowthTraceStep[] }>(`/admin/growth/trace?${q.toString()}`)
}

export interface TodayItem {
  accountId: string
  email: string
  name: string | null
  reason: string
  reasonLabel: string
  evidence: string[]
  draft: CampaignContent
  unsubscribed: boolean
  lastEmailedAt: number | null
}

export interface GrowthTouch {
  id: string
  accountId: string
  email: string | null
  reason: string
  reasonLabel: string
  kind: 'email' | 'skip'
  subject: string | null
  campaignId: string | null
  createdAt: number
  createdBy: string
  skipUntil: number | null
  delivery: string | null
  after: { signedIn: boolean; checkout: boolean; paying: boolean; payingSinceTouch: boolean }
  repliedAt: number | null
}


export interface TodayReport {
  generatedAt: number
  canSend: boolean
  items: TodayItem[]
  byReason: Array<{ reason: string; label: string } & GrowthCounted>
  recent: GrowthTouch[]
  newLeads: number
}

export async function getGrowthToday() {
  return request<{ ok: true; today: TodayReport }>('/admin/growth/today')
}

export async function sendTodayNote(accountId: string, reason: string, content: CampaignContent) {
  return request<{ ok: true; touch: GrowthTouch }>('/admin/growth/today/send', { method: 'POST', body: JSON.stringify({ accountId, reason, content }) })
}

export async function skipTodayPerson(accountId: string, reason: string, days: number) {
  return request<{ ok: true; touch: GrowthTouch }>('/admin/growth/today/skip', { method: 'POST', body: JSON.stringify({ accountId, reason, days }) })
}

export async function markTouchReplied(id: string, replied: boolean) {
  return request<{ ok: true; touch: GrowthTouch }>(`/admin/growth/touches/${encodeURIComponent(id)}/replied`, { method: 'POST', body: JSON.stringify({ replied }) })
}

// ── Prospects ───────────────────────────────────────────────────────────────
// People we want to reach who are not users and have never sent us a form, the
// outreach against them, the campaigns they are subscribed to, and the ad
// platform match lists (server: focusbuddy-signal/src/prospects.ts).
//
// Nothing in here sends anything to anyone. The server has no transport for a
// prospect: logging a touch records that a person did it by hand.

export const PROSPECT_STATUSES = [
  'not_started', 'warming', 'requested', 'connected', 'replied', 'converted', 'declined', 'stopped'
] as const
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number]

export const PROSPECT_STATUS_LABEL: Record<ProspectStatus, string> = {
  not_started: 'Not started',
  warming: 'Warming up',
  requested: 'Request sent',
  connected: 'Connected',
  replied: 'They replied',
  converted: 'Signed up',
  declined: 'Said no',
  stopped: 'Stopped'
}

export const PROSPECT_WARMUPS = ['not_yet', 'followed', 'commented', 'they_replied'] as const
export type ProspectWarmup = (typeof PROSPECT_WARMUPS)[number]
export const PROSPECT_WARMUP_LABEL: Record<ProspectWarmup, string> = {
  not_yet: 'Not yet',
  followed: 'Followed',
  commented: 'Commented',
  they_replied: 'They replied to a comment'
}

export const PROSPECT_TRACKS = ['founder_led', 'rep', 'unassigned'] as const
export type ProspectTrack = (typeof PROSPECT_TRACKS)[number]
export const PROSPECT_TRACK_LABEL: Record<ProspectTrack, string> = {
  founder_led: 'Founder-led',
  rep: 'Rep',
  unassigned: 'Unassigned'
}

export const FACT_CHECKS = ['unchecked', 'pass', 'fixed', 'unverifiable'] as const
export type FactCheck = (typeof FACT_CHECKS)[number]
export const FACT_CHECK_LABEL: Record<FactCheck, string> = {
  unchecked: 'Not checked',
  pass: 'Checked, held up',
  fixed: 'Checked, rewritten',
  unverifiable: 'Could not verify'
}

export const TOUCH_CHANNELS = ['comment', 'note', 'dm', 'email', 'form', 'newsletter', 'other'] as const
export type TouchChannel = (typeof TOUCH_CHANNELS)[number]
export const TOUCH_CHANNEL_LABEL: Record<TouchChannel, string> = {
  comment: 'Comment on their post',
  note: 'Connection request',
  dm: 'Direct message',
  email: 'Email',
  form: 'Contact form',
  newsletter: 'Newsletter reply',
  other: 'Something else'
}

export const TOUCH_OUTCOMES = ['sent', 'accepted', 'replied', 'no_reply', 'declined', 'bounced'] as const
export type TouchOutcome = (typeof TOUCH_OUTCOMES)[number]
export const TOUCH_OUTCOME_LABEL: Record<TouchOutcome, string> = {
  sent: 'Sent',
  accepted: 'They accepted',
  replied: 'They replied',
  no_reply: 'No reply',
  declined: 'They said no',
  bounced: 'Did not reach them'
}

export const AD_PLATFORMS = ['google', 'facebook', 'instagram', 'tiktok', 'linkedin'] as const
export type AdPlatform = (typeof AD_PLATFORMS)[number]
export const AD_PLATFORM_LABEL: Record<AdPlatform, string> = {
  google: 'Google',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn'
}

export const AUDIENCE_STATUSES = ['not_uploaded', 'uploaded', 'matched', 'unmatched', 'removed'] as const
export type AudienceStatus = (typeof AUDIENCE_STATUSES)[number]
export const AUDIENCE_STATUS_LABEL: Record<AudienceStatus, string> = {
  not_uploaded: 'Not uploaded',
  uploaded: 'Uploaded, no result yet',
  matched: 'Matched',
  unmatched: 'Uploaded, not matched',
  removed: 'Removed from the list'
}

export const CAMPAIGN_STATUSES = ['draft', 'active', 'paused', 'archived'] as const
export type ProspectCampaignStatus = (typeof CAMPAIGN_STATUSES)[number]

export interface RemoteProspectAudience {
  platform: AdPlatform
  uploadsVia: string
  matchKey: string | null
  matchBlockedBy: string | null
  status: AudienceStatus
  audienceName: string | null
  uploadedAt: string | null
  resultNote: string | null
  observedClickId: string | null
  updatedAt: string | null
}

export interface RemoteProspectCampaignMembership {
  campaignId: string
  name: string
  channel: string
  campaignStatus: ProspectCampaignStatus
  state: 'subscribed' | 'paused' | 'completed' | 'unsubscribed'
  subscribedAt: string
  unsubscribedAt: string | null
}

export interface RemoteProspectTouch {
  id: string
  prospectId: string
  channel: TouchChannel
  direction: 'out' | 'in'
  outcome: TouchOutcome | null
  body: string | null
  adminId: string | null
  adminEmail: string | null
  campaignId: string | null
  campaignName: string | null
  emailSendId: string | null
  occurredAt: string
  createdAt: string
}

export type ReachStep = 'hold' | 'done' | 'verify' | 'reply' | 'start_there' | 'connect' | 'email' | 'form' | 'wait' | 'message' | 'follow_up' | 'no_route'

export interface ReachPlan {
  step: ReachStep
  title: string
  why: string
  message: 'note' | 'dm' | 'email' | null
  actionable: boolean
  channels: Array<{ label: string; url: string | null; how: string }>
  factsChecked: boolean
}

export interface RemoteProspect {
  id: string
  name: string
  linkedinUrl: string | null
  urlSource: string | null
  email: string | null
  role: string | null
  company: string | null
  location: string | null
  country: string | null
  segment: string | null
  subNiche: string | null
  contentFocus: string | null
  followers: string | null
  followerSource: string | null
  fitScore: number | null
  responsivenessScore: number | null
  score: number | null
  tier: 'A' | 'B' | 'C' | null
  confidence: string | null
  evidenceType: string | null
  activityEvidence: string | null
  responsivenessSignals: string | null
  whyPlexii: string | null
  hook: string | null
  otherChannels: string | null
  watchOuts: string | null
  factCheck: FactCheck
  factCheckNote: string | null
  draftNote: string | null
  draftDm: string | null
  draftEmailSubject: string | null
  draftEmail: string | null
  status: ProspectStatus
  warmup: ProspectWarmup
  track: ProspectTrack
  ownerAdminId: string | null
  ownerAdminEmail: string | null
  doNotSend: boolean
  doNotSendReason: string | null
  verified: boolean
  checkedBy: string | null
  checkedAt: string | null
  personaId: string | null
  personaName: string | null
  emailSource: string | null
  contactFormUrl?: string | null
  plan: ReachPlan
  batch: string | null
  source: string | null
  externalId: string | null
  notes: string | null
  nextActionAt: string | null
  lastTouchAt: string | null
  touchCount: number
  createdAt: string
  updatedAt: string
  audiences: RemoteProspectAudience[]
  campaigns: RemoteProspectCampaignMembership[]
}

export interface ProspectCounts {
  total: number
  byStatus: Record<ProspectStatus, number>
  byTier: Record<'A' | 'B' | 'C' | 'none', number>
  byFactCheck: Record<FactCheck, number>
  doNotSend: number
  unverified: number
  withEmail: number
  dueToday: number
}

export interface RemoteProspectCampaign {
  id: string
  name: string
  channel: string
  description: string | null
  status: ProspectCampaignStatus
  subscribed: number
  createdAt: string
  updatedAt: string
}

export interface AudienceSummary {
  platform: AdPlatform
  uploadsVia: string
  matchable: number
  unmatchable: number
  byStatus: Record<AudienceStatus, number>
  observedClicks: number
}

export async function listProspects(
  params: { status?: string; tier?: string; factCheck?: string; campaignId?: string; personaId?: string; due?: boolean; q?: string; limit?: number } = {}
) {
  const qs = new URLSearchParams()
  if (params.status && params.status !== 'all') qs.set('status', params.status)
  if (params.tier && params.tier !== 'all') qs.set('tier', params.tier)
  if (params.factCheck && params.factCheck !== 'all') qs.set('factCheck', params.factCheck)
  if (params.campaignId) qs.set('campaignId', params.campaignId)
  if (params.personaId) qs.set('personaId', params.personaId)
  if (params.due) qs.set('due', '1')
  if (params.q) qs.set('q', params.q)
  if (params.limit) qs.set('limit', String(params.limit))
  return request<{
    ok: true
    prospects: RemoteProspect[]
    total: number
    counts: ProspectCounts
    campaigns: RemoteProspectCampaign[]
    audiences: AudienceSummary[]
    personas?: PersonaView[]
  }>(`/admin/prospects${qs.toString() ? `?${qs}` : ''}`)
}

export interface FactLine {
  key: string
  label: string
  text: string | null
  sources: string[]
  note: string | null
  tone: 'warn' | 'check' | null
}

export interface StyleIssue {
  rule: string
  found: string
  why: string
  level: 'block' | 'warn'
}

export interface DraftHistoryEntry {
  id: string
  draftNote: string | null
  draftDm: string | null
  draftEmailSubject?: string | null
  draftEmail?: string | null
  replacedAt: string
  replacedBy: string | null
  reason: string | null
}

export interface SignatureView {
  id: string
  name: string
  body: string
  isDefault: boolean
  updatedAt: string
}

export async function getProspect(id: string) {
  return request<{
    ok: true
    prospect: RemoteProspect
    touches: RemoteProspectTouch[]
    facts?: FactLine[]
    draftHistory?: DraftHistoryEntry[]
    style?: { note: StyleIssue[]; dm: StyleIssue[] }
    emailBlocker?: string | null
    emailCaution?: string | null
    signatures?: SignatureView[]
  }>(
    `/admin/prospects/${encodeURIComponent(id)}`
  )
}

export async function patchProspect(id: string, patch: Record<string, unknown>) {
  return request<{ ok: true; prospect: RemoteProspect }>(`/admin/prospects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  })
}

export async function deleteProspect(id: string) {
  return request<{ ok: true }>(`/admin/prospects/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function addProspectTouch(
  id: string,
  touch: { channel: TouchChannel; direction: 'out' | 'in'; outcome?: TouchOutcome | ''; body?: string; campaignId?: string | null }
) {
  return request<{ ok: true; touch: RemoteProspectTouch; prospect: RemoteProspect; touches: RemoteProspectTouch[] }>(
    `/admin/prospects/${encodeURIComponent(id)}/touches`,
    { method: 'POST', body: JSON.stringify(touch) }
  )
}

export async function importProspects(items: Array<Record<string, unknown>>, source: string, batch: string) {
  return request<{ ok: true; imported: number; duplicates: number; errors: Array<{ index: number; error: string }> }>(
    '/admin/prospects/import',
    { method: 'POST', body: JSON.stringify({ prospects: items, source, batch }) }
  )
}

export async function createProspectCampaign(campaign: { name: string; channel: string; description?: string; personaId?: string | null; goal?: string }) {
  return request<{ ok: true; campaign: RemoteProspectCampaign }>('/admin/prospect-campaigns', {
    method: 'POST',
    body: JSON.stringify(campaign)
  })
}

export async function patchProspectCampaign(id: string, patch: { name?: string; description?: string; status?: string }) {
  return request<{ ok: true; campaign: RemoteProspectCampaign }>(`/admin/prospect-campaigns/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  })
}

export async function subscribeProspects(campaignId: string, prospectIds: string[]) {
  return request<{ ok: true; subscribed: number; refused: Array<{ id: string; reason: string }>; campaign: RemoteProspectCampaign }>(
    `/admin/prospect-campaigns/${encodeURIComponent(campaignId)}/subscribe`,
    { method: 'POST', body: JSON.stringify({ prospectIds }) }
  )
}

export async function setProspectCampaignState(campaignId: string, prospectId: string, state: string) {
  return request<{ ok: true; campaign: RemoteProspectCampaign }>(
    `/admin/prospect-campaigns/${encodeURIComponent(campaignId)}/members/${encodeURIComponent(prospectId)}`,
    { method: 'POST', body: JSON.stringify({ state }) }
  )
}

export async function exportAudience(platform: AdPlatform, campaignId?: string) {
  const qs = new URLSearchParams({ platform })
  if (campaignId) qs.set('campaignId', campaignId)
  return request<{ ok: true; platform: AdPlatform; uploadsVia: string; header: string[]; rows: string[][]; skippedNoEmail: number }>(
    `/admin/prospects/audiences/export?${qs}`
  )
}

export async function recordAudienceResult(
  prospectId: string,
  platform: AdPlatform,
  result: { status: AudienceStatus; audienceName?: string; resultNote?: string }
) {
  return request<{ ok: true; prospect: RemoteProspect }>(
    `/admin/prospects/${encodeURIComponent(prospectId)}/audiences/${platform}`,
    { method: 'POST', body: JSON.stringify(result) }
  )
}

// ── Outreach: style, email, signatures, personas, research (server: outreach.ts, outreachAi.ts) ──

export async function getOutreachStyle() {
  return request<{ ok: true; guide: Record<'note' | 'dm' | 'email', { title: string; rules: string[] }>; limits: { note: number; dm: number } }>('/admin/outreach/style')
}

export async function checkOutreachText(text: string, kind: 'note' | 'dm' | 'email', name?: string) {
  return request<{ ok: true; issues: StyleIssue[] }>('/admin/outreach/check', { method: 'POST', body: JSON.stringify({ text, kind, name }) })
}

export async function previewProspectEmail(id: string, mail: { subject: string; body: string; signatureId?: string | null }) {
  return request<{ ok: true; subject: string; html: string; text: string; issues: StyleIssue[]; error: string | null; blocker: string | null; to: string | null }>(
    `/admin/prospects/${encodeURIComponent(id)}/email/preview`,
    { method: 'POST', body: JSON.stringify(mail) }
  )
}

export async function sendProspectEmail(id: string, mail: { subject: string; body: string; signatureId?: string | null; campaignId?: string | null }) {
  return request<{ ok: true; prospect: RemoteProspect; touches: RemoteProspectTouch[]; delivered: boolean; provider: string }>(
    `/admin/prospects/${encodeURIComponent(id)}/email`,
    { method: 'POST', body: JSON.stringify(mail) }
  )
}

export async function replaceProspectDrafts(items: Array<{ id: string; note?: string; dm?: string }>, reason: string) {
  return request<{ ok: true; updated: number; unchanged: number; refused: Array<{ id: string; error: string }> }>('/admin/prospects/drafts', {
    method: 'POST',
    body: JSON.stringify({ items, reason })
  })
}

export async function listSignatures() {
  return request<{ ok: true; signatures: SignatureView[] }>('/admin/signatures')
}

export async function saveSignature(sig: { id?: string; name: string; body: string; isDefault?: boolean }) {
  return request<{ ok: true; signature: SignatureView; signatures: SignatureView[] }>('/admin/signatures', { method: 'POST', body: JSON.stringify(sig) })
}

export async function deleteSignature(id: string) {
  return request<{ ok: true; signatures: SignatureView[] }>(`/admin/signatures/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export const PERSONA_PROFILE_FIELDS = [
  ['summary', 'In one line'],
  ['who', 'Who they are'],
  ['pains', 'What they struggle with'],
  ['goals', 'What they want'],
  ['whereTheyAre', 'Where they gather'],
  ['triggers', 'What makes them look for help'],
  ['objections', 'What makes them hesitate'],
  ['theirWords', 'Words they use']
] as const
export const PERSONA_MESSAGING_FIELDS = [
  ['angle', 'The honest reason we are writing'],
  ['avoid', 'Never say or do'],
  ['noteGuide', 'Connection notes for this group'],
  ['dmGuide', 'First messages for this group'],
  ['emailGuide', 'Emails for this group']
] as const
export type PersonaFieldKey = (typeof PERSONA_PROFILE_FIELDS)[number][0] | (typeof PERSONA_MESSAGING_FIELDS)[number][0] | 'findCriteria'

export type PersonaView = { id: string; name: string; status: 'active' | 'archived'; prospects: number; campaigns: number; research: number; createdAt: string; updatedAt: string } & Record<PersonaFieldKey, string | null>

export interface ResearchView {
  id: string
  personaId: string
  kind: 'ai' | 'note'
  title: string
  body: { summary: string | null; sections: Array<{ heading: string; points: Array<{ text: string; sources: Array<{ url: string; title: string | null }> }> }>; dropped?: number }
  jobId: string | null
  createdBy: string | null
  createdAt: string
}

export interface OutreachJob {
  id: string
  kind: 'research' | 'find' | 'emails'
  personaId: string | null
  status: 'running' | 'done' | 'failed'
  params: Record<string, unknown>
  result: Record<string, unknown> | null
  error: string | null
  model: string | null
  inputTokens: number
  outputTokens: number
  searches: number
  costUsd: number
  createdBy: string | null
  createdAt: string
  finishedAt: string | null
}

export interface OutreachAiInfo {
  model: string
  available: boolean
  spentUsd: number
  findMax: number
  emailsMax?: number
}

export async function listPersonas() {
  return request<{ ok: true; personas: PersonaView[]; ai: OutreachAiInfo }>('/admin/personas')
}

export async function startFindEmails(input: { tier?: string | null; limit: number; prospectIds?: string[]; useAi?: boolean }) {
  return request<{ ok: true; job: OutreachJob }>('/admin/outreach/find-emails', { method: 'POST', body: JSON.stringify(input) })
}

export async function getSendStatus() {
  return request<{ ok: true; sentToday: number; cap: number }>('/admin/outreach/send-status')
}

export async function listOutreachJobs() {
  return request<{ ok: true; jobs: OutreachJob[]; spentUsd: number }>('/admin/outreach/jobs')
}

export async function getPersona(id: string) {
  return request<{ ok: true; persona: PersonaView & { researchEntries: ResearchView[] }; jobs: OutreachJob[]; campaigns: RemoteProspectCampaign[] }>(
    `/admin/personas/${encodeURIComponent(id)}`
  )
}

export async function createPersona(p: Partial<Record<PersonaFieldKey | 'name', string>>) {
  return request<{ ok: true; persona: PersonaView }>('/admin/personas', { method: 'POST', body: JSON.stringify(p) })
}

export async function patchPersona(id: string, p: Partial<Record<PersonaFieldKey | 'name' | 'status', string | null>>) {
  return request<{ ok: true; persona: PersonaView }>(`/admin/personas/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(p) })
}

export async function deletePersona(id: string) {
  return request<{ ok: true }>(`/admin/personas/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function assignPersona(personaId: string | 'none', prospectIds: string[]) {
  return request<{ ok: true; linked: number }>(`/admin/personas/${encodeURIComponent(personaId)}/assign`, { method: 'POST', body: JSON.stringify({ prospectIds }) })
}

export async function addPersonaNote(personaId: string, note: { title: string; text: string; sources: string[] }) {
  return request<{ ok: true; research: ResearchView }>(`/admin/personas/${encodeURIComponent(personaId)}/notes`, { method: 'POST', body: JSON.stringify(note) })
}

export async function deletePersonaResearch(id: string) {
  return request<{ ok: true }>(`/admin/persona-research/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function startPersonaResearch(personaId: string, focus: string) {
  return request<{ ok: true; job: OutreachJob }>(`/admin/personas/${encodeURIComponent(personaId)}/research`, { method: 'POST', body: JSON.stringify({ focus }) })
}

export async function startFindPeople(personaId: string, count: number, criteria: string) {
  return request<{ ok: true; job: OutreachJob }>(`/admin/personas/${encodeURIComponent(personaId)}/find`, { method: 'POST', body: JSON.stringify({ count, criteria }) })
}

export async function getOutreachJob(id: string) {
  return request<{ ok: true; job: OutreachJob }>(`/admin/outreach/jobs/${encodeURIComponent(id)}`)
}

export interface ProspectCampaignDetail {
  id: string
  name: string
  channel: string
  description: string | null
  status: ProspectCampaignStatus
  personaId: string | null
  personaName: string | null
  goal: string | null
  noteTemplate: string | null
  dmTemplate: string | null
  emailSubject: string | null
  emailTemplate: string | null
  followUpDays: number | null
  createdAt: string
  updatedAt: string
  stages: Array<{ key: string; label: string; count: number }>
  members: Array<{ id: string; name: string; company: string | null; status: ProspectStatus; step: string; lastTouchAt: string | null; touches: number }>
  events: Array<RemoteProspectTouch & { prospectName: string }>
}

export async function getProspectCampaign(id: string) {
  return request<{ ok: true; campaign: ProspectCampaignDetail }>(`/admin/prospect-campaigns/${encodeURIComponent(id)}`)
}

export async function saveProspectCampaign(id: string, patch: Record<string, unknown>) {
  return request<{ ok: true; campaign: RemoteProspectCampaign }>(`/admin/prospect-campaigns/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) })
}

// ── Target markets ──────────────────────────────────────────────────────────
// How outreach to each market is going. Every figure is a count of real rows,
// from the same reachPlan the person view uses. A market nobody has written to
// has `replyRate: null`, never 0.

export interface MarketWork {
  step: string
  title: string
  count: number
}

export interface MarketView {
  key: string
  people: number
  contacted: number
  replied: number
  signedUp: number
  saidNo: number
  held: number
  readyNow: number
  work: MarketWork[]
  reach: { email: number; form: number; linkedin: number; none: number }
  /** null until something has actually been sent to this market. */
  replyRate: number | null
  lastSentAt: string | null
  personas: Array<{ id: string; name: string; people: number }>
  unchecked: number
}

export interface MarketsReport {
  markets: MarketView[]
  noMarket: number
  totals: { people: number; contacted: number; replied: number; readyNow: number }
}

export async function listMarkets() {
  return request<{ ok: true } & MarketsReport>('/admin/prospects/markets')
}
